import { computed } from 'vue'
import { useAuthStore } from '@/modules/auth'
import {
  QVARA_ROLE_ENUMS,
  RESELLER_ROLE_ENUMS,
  USER_ROLES,
  USER_ROLE_TYPES
} from '../constants/permissions'

/**
 * Composable to handle permission and role-based access control (RBAC) checks
 * throughout the application.
 */
export function usePermissions() {
  // Auth store: logged-in user, permissions, and allowances for RBAC checks.
  const authStore = useAuthStore()

  /**
   * Check if the current user has a specific permission or allowance.
   * Supports hierarchical checks (e.g., 'system' will match 'system.users.view').
   */
  const can = (permission?: string) => {
    if (!permission) return true

    // Get permissions from user object
    const user = authStore.user
    const userPermissions = user?.permissions || []
    const userAllowances = user?.allowances || []

    // Check for exact match in either permissions or allowances
    if (userPermissions.includes(permission) || userAllowances.includes(permission)) {
      return true
    }

    // Check for prefix match (e.g. 'uc_user' should match 'uc_user.automations.view')
    // This allows showing a module if the user has ANY permission within it
    const allPerms = [...userPermissions, ...userAllowances]
    const hasPrefixMatch =
      allPerms.some((p: string) => p.startsWith(`${permission}.`)) ||
      allPerms.some((p: string) => p.startsWith(`${permission}:`))

    return hasPrefixMatch
  }

  /**
   * Check if the current user has any of the provided permissions.
   */
  const canAny = (permissions: string[]) => {
    return permissions.some((p) => can(p))
  }

  /**
   * Check if the current user has a specific role type (e.g., 'qv', 'rs').
   */
  const hasRole = (roleType?: string | string[]) => {
    if (!roleType) return true

    const user = authStore.user
    const userRoleType = user?.role_type
    const userRoleEnum = user?.role_enum || ''

    const checkRole = (role: string) => {
      if (userRoleType === role) return true

      // Fallback for role_enum if role_type is missing or to support legacy checks
      if (role === USER_ROLE_TYPES.QVARA) return QVARA_ROLE_ENUMS.includes(userRoleEnum)
      if (role === USER_ROLE_TYPES.RESELLER) return RESELLER_ROLE_ENUMS.includes(userRoleEnum)
      if (role === USER_ROLE_TYPES.ACCOUNT) return userRoleEnum === USER_ROLES.ACCOUNT_ADMIN
      if (role === USER_ROLE_TYPES.BASIC_USER) return userRoleEnum === USER_ROLES.BASIC_USER

      return false
    }

    if (Array.isArray(roleType)) {
      return roleType.some((r) => checkRole(r))
    }

    return checkRole(roleType)
  }

  /**
   * Check if the application is running in development mode.
   */
  const isDev = computed(() => import.meta.env.DEV)

  /**
   * High-level visibility check combining environment, permissions, and roles.
   * Useful for navigation items and action buttons.
   */
  const isVisible = (options: {
    permission?: string
    roleType?: string | string[]
    devOnly?: boolean
  }) => {
    if (options.devOnly && !isDev.value) return false
    if (options.permission && !can(options.permission)) return false
    if (options.roleType && !hasRole(options.roleType)) return false
    return true
  }

  /**
   * Check if the current user has a specific allowance.
   * Following legacy rules, non-basic users automatically have all allowances.
   */
  const hasAllowance = (allowance: string) => {
    const isBasic = hasRole('bu')
    if (!isBasic) return true

    return authStore.user?.allowances?.includes(allowance) ?? false
  }

  return {
    can,
    canAny,
    hasRole,
    hasAllowance,
    isDev,
    isVisible,
    user: computed(() => authStore.user)
  }
}
